import { useState, useRef, useEffect, useContext } from "react";
import { useQueryClient } from "react-query";
import * as api from "../services";
import { getUnique, logging } from "../tools";
import AppContext from "../appContext";

// This custom hook centralizes and streamlines handling of beep calls
export default function useBeeps(beepIds, enabled = true) {
  const queryClient = useQueryClient();
  const {
    app: {
      client: { id: clientId },
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);

  const [beeps, setBeeps] = useState(undefined);
  const [isBeepLoading, setisBeepLoading] = useState(true);
  const [isBeepError, setIsBeepError] = useState(false);
  const prevEnabled = useRef();
  const prevBeepIds = useRef();

  useEffect(() => {
    // Only refetch if enabled params change.
    if (
      JSON.stringify(prevBeepIds.current) === JSON.stringify(beepIds) &&
      prevEnabled.current === enabled
    ) {
      return;
    }
    // beepIds must be an array
    if (!Array.isArray(beepIds)) return;

    prevEnabled.current = enabled;
    prevBeepIds.current = beepIds;

    if (enabled) {
      const cachedBeepIds = beepIds.filter(
        (id) => queryClient.getQueryCache().find(["beep", id])?.state?.data
      );
      const idsToFetch = beepIds.filter(
        (beepId) => !cachedBeepIds.includes(beepId)
      );
      // get cached beepList and interactions from memory
      const cachedBeeps = cachedBeepIds.map(
        (id) => queryClient.getQueryCache().find(["beep", id]).state.data
      );
      const cachedBeepList = cachedBeeps.map(({ beep }) => beep);
      const cachedInteractions = getUnique(
        cachedBeeps.map(({ interaction }) => interaction),
        "questionListLink"
      ).reduce((acc, cur) => ({ ...acc, [cur.questionListLink]: cur }), {});

      // new and cached beeps
      if (idsToFetch.length > 0 && cachedBeepIds.length >= 0) {
        logging(`FETCH BEEPS ${idsToFetch} FROM SERVER`);
        api
          .getBeeps({
            clientId,
            beepIdList: JSON.stringify(idsToFetch),
            projectId,
            sessionId,
          })
          .then(function (response) {
            // save it in the cache
            //console.log(response);
            response.beepList.map((beep) => {
              const interaction = {
                ...response.interactions[beep.questionListLink],
                questionListLink: beep.questionListLink,
              };
              return queryClient.setQueryData(["beep", beep.id], {
                beep,
                interaction,
              });
            });
            setBeeps((prev) => ({
              ...prev,
              status: response.status,
              beepList: [...response.beepList, ...cachedBeepList],
              interactions: {
                ...response.interactions,
                ...cachedInteractions,
              },
            }));

            setisBeepLoading(false);
          })
          .catch(function (error) {
            console.error(error);
            setIsBeepError(true);
            setisBeepLoading(false);
          });
      }

      // only cached beeps
      if (idsToFetch.length === 0 && cachedBeepIds.length >= 0) {
        //console.log(`CACHED BEEPS ${cachedBeepIds} FROM MEMORY`);
        setBeeps((prev) => ({
          ...prev,
          status: 1,
          beepList: cachedBeepList,
          interactions: cachedInteractions,
        }));
        setisBeepLoading(false);
      }
    }

    return () => {};
  }, [beepIds, enabled]);

  return { beeps, isBeepLoading, isBeepError };
}
